import { getAccessToken } from '../services';
import {
  DPOP_NONCE_SCOPE,
  buildAuthorizationHeaders,
  isDpopNonceRequired,
  rememberDpopNonce,
  reportDpopNonceChallenge,
} from '../services/auth/dpop';
import { config } from '../config';
import { getUserAgent } from './utils/api';

const jsonHeaders = () => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'User-Agent': getUserAgent(),
  'X-User-Agent': getUserAgent(),
});

/**
 * The two passkey-registration calls present the access token outside
 * apiFetch, so they go through the same DPoP seam: `Bearer` or `DPoP` + proof
 * by the token, and one answer to a `use_dpop_nonce` challenge (RFC 9449 §9)
 * with a fresh proof carrying the nonce the API named. No refresh and no other
 * retry — those stay apiFetch's job.
 */
const fetchWithAccessToken = async (
  url,
  { method, body },
  retriedWithNonce = false
) => {
  const accessToken = await getAccessToken();
  const authHeaders = await buildAuthorizationHeaders({
    accessToken,
    method,
    url,
  });

  const options = {
    method,
    headers: new Headers({ ...authHeaders, ...jsonHeaders() }),
  };
  if (body !== undefined) {
    options.body = body;
  }
  const response = await fetch(url, options);

  const nonce = rememberDpopNonce(DPOP_NONCE_SCOPE.API, response.headers);
  if (
    authHeaders.DPoP &&
    nonce &&
    !retriedWithNonce &&
    isDpopNonceRequired(response)
  ) {
    reportDpopNonceChallenge({ endpointKind: 'api', htu: url });
    return fetchWithAccessToken(url, { method, body }, true);
  }

  return response.json();
};

export const webAuthn = {
  generateRegistrationOptions: () =>
    fetchWithAccessToken(
      `${config.api.host}/auth/webauthn/generate-registration-options`,
      { method: 'GET' }
    ),

  verifyRegistration: (data) =>
    fetchWithAccessToken(
      `${config.api.host}/auth/webauthn/verify-registration`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  generateAuthenticationOptions: async () => {
    const url = `${config.api.host}/auth/webauthn/generate-authentication-options`;
    const headers = new Headers(jsonHeaders());
    const options = {
      method: 'GET',
      headers,
    };
    const response = await fetch(url, options);
    const sessionId = response.headers.get('x-webauthn-session-id');

    return {
      options: await response.json(),
      sessionId,
    };
  },

  /**
   * `responseType: 'login_assertion'` asks the API for a single-use login
   * assertion (`{ verified, login_assertion }`) to exchange at /oidc/token,
   * instead of the legacy `{ verified, token }` body.
   */
  verifyAuthentication: async (data, sessionId, { responseType } = {}) => {
    let url = `${config.api.host}/auth/webauthn/verify-authentication`;
    if (responseType) {
      url += `?response=${encodeURIComponent(responseType)}`;
    }

    const headers = new Headers({
      ...jsonHeaders(),
      'X-WebAuthn-Session-Id': sessionId,
    });

    const options = {
      method: 'POST',
      body: JSON.stringify(data),
      headers: headers,
    };
    const response = await fetch(url, options);
    return response.json();
  },
};
